import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const NAV = [
  { label: "Startseite", href: "#home", id: "home" },
  { label: "Für Versorger", href: "#versorger", id: "versorger" },
  { label: "Leistungen", href: "#leistungen", id: "leistungen" },
  { label: "Qualität", href: "#qualitaet", id: "qualitaet" },
  { label: "Akademie", href: "#akademie", id: "akademie" },
  { label: "Über uns", href: "#ueber-uns", id: "ueber-uns" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 group"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <div className="h-11 w-11 md:h-14 md:w-14 rounded-lg overflow-hidden shadow-md ring-1 ring-black/5 bg-[#0A0F0C] flex items-center justify-center">
              <img
                src={ASSETS.logo}
                alt="faktor-direkt Logo"
                className="h-full w-full object-cover scale-110"
                data-testid="header-logo-img"
              />
            </div>
            <span
              className="text-lg md:text-xl font-bold tracking-tight text-[#0A3B2C]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              faktor-direkt
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="header-desktop-nav">
            {NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => goTo(item.href)}
                data-testid={`nav-link-${item.id}`}
                className="text-sm font-medium text-gray-700 hover:text-[#0A3B2C] transition-colors relative group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#0A3B2C] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => goTo("#kontakt")}
              data-testid="header-cta-kontakt"
              className="hidden md:inline-flex items-center bg-[#0A3B2C] text-white hover:bg-[#072A1F] transition-all px-5 py-2.5 font-semibold rounded-md text-sm shadow-sm hover:shadow-md"
            >
              Kontakt
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md text-[#0A3B2C] hover:bg-gray-100 transition"
              data-testid="header-mobile-toggle"
              aria-label="Menü"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {open && (
          <div className="lg:hidden pb-6 pt-2 border-t border-gray-100" data-testid="header-mobile-nav">
            <div className="flex flex-col gap-1 pt-3">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  onClick={() => goTo(item.href)}
                  data-testid={`nav-mobile-link-${item.id}`}
                  className="text-left px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 rounded-md"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => goTo("#kontakt")}
                data-testid="header-mobile-cta"
                className="mt-3 bg-[#0A3B2C] text-white px-5 py-3 font-semibold rounded-md text-sm"
              >
                Kontakt
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
